import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { Page, Storybook } from '../../types/storybook';
import { storybookAPI } from '../../utils/api';
import { clampPageIndex } from './pageDomain';

interface QueuedSave<Input, Output> {
  input: Input;
  resolve: (output: Output) => void;
  reject: (error: unknown) => void;
}

export interface EditorSaveConflict {
  code: 'STORYBOOK_REVISION_CONFLICT';
  message: string;
  expectedRevision?: number;
  currentRevision?: number;
}

export class SerializedSaveQueue<Input, Output> {
  private queue: Array<QueuedSave<Input, Output>> = [];
  private running = false;

  constructor(private readonly worker: (input: Input) => Promise<Output>) {}

  get hasPending(): boolean {
    return this.running || this.queue.length > 0;
  }

  get pendingCount(): number {
    return this.queue.length + (this.running ? 1 : 0);
  }

  enqueue(input: Input): Promise<Output> {
    const promise = new Promise<Output>((resolve, reject) => {
      this.queue.push({ input, resolve, reject });
    });
    void this.drain();
    return promise;
  }

  private async drain(): Promise<void> {
    if (this.running) return;
    this.running = true;
    try {
      while (this.queue.length > 0) {
        const item = this.queue.shift();
        if (!item) continue;
        try {
          item.resolve(await this.worker(item.input));
        } catch (error) {
          item.reject(error);
        }
      }
    } finally {
      this.running = false;
      // A microtask can enqueue work after the loop condition but before the
      // running flag is cleared. Make that edge deterministic as well.
      if (this.queue.length > 0) void this.drain();
    }
  }
}

/** Coalesces only identical save intents while allowing a newer edited state to queue. */
export class MatchingInFlightSave<Output> {
  private current?: { key: string; promise: Promise<Output> };

  run(key: string, worker: () => Promise<Output>): Promise<Output> {
    if (this.current?.key === key) return this.current.promise;
    const promise = Promise.resolve().then(worker);
    this.current = { key, promise };
    void promise.finally(() => {
      if (this.current?.promise === promise) this.current = undefined;
    }).catch(() => undefined);
    return promise;
  }
}

type EditorAutosaveSnapshot = Record<string, unknown>;

const comparablePages = (pages: Page[]): Page[] => pages.map(page => ({
  ...page,
  thumbnail: undefined,
  revision: undefined,
  generationOperationKey: undefined,
  generationOperationIds: undefined,
  generationFence: undefined
}));

/**
 * Returns only user-owned editor state. Server concurrency metadata and Quick
 * Create approvals have separate owners and must never make an autosave dirty.
 */
export const createEditorAutosaveSnapshot = (storybook: Storybook): EditorAutosaveSnapshot => {
  const {
    _id,
    userId: _userId,
    revision: _revision,
    generationSourceRevision: _generationSourceRevision,
    quickCreate: _quickCreate,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    ...editorState
  } = storybook;
  return {
    ...editorState,
    pages: comparablePages(storybook.pages)
  };
};

const valuesMatch = (left: unknown, right: unknown): boolean => (
  JSON.stringify(left) === JSON.stringify(right)
);

/**
 * Computes a top-level merge patch while treating `pages` as one ordered
 * document domain. Values come from the live book (so thumbnails are retained)
 * while comparisons use the server-owned-field-free snapshot above.
 */
export const createEditorAutosavePatch = (
  previous: EditorAutosaveSnapshot,
  storybook: Storybook
): Partial<Storybook> => {
  const current = createEditorAutosaveSnapshot(storybook);
  const patch: Record<string, unknown> = {};
  Object.keys(current).forEach(key => {
    if (!valuesMatch(previous[key], current[key])) {
      patch[key] = key === 'pages' ? storybook.pages : (storybook as unknown as Record<string, unknown>)[key];
    }
  });
  return patch as Partial<Storybook>;
};

/** Backward-compatible diagnostic helper; autosave itself uses patches. */
export const createEditorAutosaveHash = (storybook: Storybook): string => (
  JSON.stringify(createEditorAutosaveSnapshot(storybook))
);

const saveConflictFrom = (error: unknown): EditorSaveConflict | null => {
  const response = (error as any)?.response;
  if (response?.status !== 409 || response?.data?.code !== 'STORYBOOK_REVISION_CONFLICT') return null;
  return {
    code: 'STORYBOOK_REVISION_CONFLICT',
    message: response.data.error || 'This book changed elsewhere. Reload it before saving again.',
    expectedRevision: response.data.expectedRevision,
    currentRevision: response.data.currentRevision
  };
};

const persistedPageMetadata = (persisted: Page) => ({
  revision: persisted.revision,
  generationOperationKey: persisted.generationOperationKey,
  generationOperationIds: persisted.generationOperationIds,
  generationFence: persisted.generationFence
});

export const adoptPersistedRevision = (
  local: Storybook | null,
  persisted: Storybook,
  thumbnailPage?: Page
): Storybook => {
  if (!local) return persisted;
  return {
    ...local,
    revision: persisted.revision,
    generationSourceRevision: persisted.generationSourceRevision,
    pages: local.pages.map(page => {
      const savedPage = persisted.pages.find(candidate => candidate.id === page.id);
      const thumbnail = thumbnailPage?.id === page.id ? thumbnailPage.thumbnail : page.thumbnail;
      return savedPage
        ? { ...page, thumbnail, ...persistedPageMetadata(savedPage) }
        : { ...page, thumbnail };
    })
  };
};

export interface EditorSaveDomain {
  saving: boolean;
  setSaving: Dispatch<SetStateAction<boolean>>;
  lastSaved: Date | null;
  setLastSaved: Dispatch<SetStateAction<Date | null>>;
  saveConflict: EditorSaveConflict | null;
  clearSaveConflict: () => void;
  saveStorybook: () => Promise<Storybook | null>;
  queueSave: (data: Partial<Storybook>) => Promise<Storybook>;
  safeSaveWithMerge: (
    mergeChanges: (freshData: Storybook) => Partial<Storybook>,
    description?: string
  ) => Promise<Storybook | null>;
  hasPendingSaves: () => boolean;
}

interface EditorSaveDomainOptions {
  storybookId?: string;
  storybook: Storybook | null;
  setStorybook: Dispatch<SetStateAction<Storybook | null>>;
  currentPageIndex: number;
  plots: Array<{ pageNumber: number; text: string }>;
  loading: boolean;
  isGenerating: () => boolean;
  generateThumbnail: (pageIndex: number) => Promise<string | null>;
  getPageContentHash: (page: Page) => string;
  onPageSaved?: (pageIndex: number, pageHash: string, thumbnail: string | null) => void;
  autosaveDelayMs?: number;
}

export const useEditorSaveDomain = (options: EditorSaveDomainOptions): EditorSaveDomain => {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const storybookRef = useRef(options.storybook);
  storybookRef.current = options.storybook;
  const [savingState, setSavingState] = useState(false);
  const savingRef = useRef(false);
  const activeSaveCountRef = useRef(0);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveConflict, setSaveConflict] = useState<EditorSaveConflict | null>(null);
  const persistedSnapshotRef = useRef<EditorAutosaveSnapshot | null>(null);
  const authoritativeRevisionRef = useRef<number | undefined>(options.storybook?.revision);
  const baselineBookIdRef = useRef<string | undefined>();
  const matchingSaveRef = useRef(new MatchingInFlightSave<Storybook | null>());

  const setSaving = useCallback<Dispatch<SetStateAction<boolean>>>((nextValue) => {
    if (typeof nextValue !== 'function') savingRef.current = nextValue;
    setSavingState(current => {
      const next = typeof nextValue === 'function' ? nextValue(current) : nextValue;
      savingRef.current = next;
      return next;
    });
  }, []);

  const queueRef = useRef<SerializedSaveQueue<Partial<Storybook>, Storybook>>();
  if (!queueRef.current) {
    queueRef.current = new SerializedSaveQueue(async data => {
      const storybookId = optionsRef.current.storybookId;
      if (!storybookId) throw new Error('Cannot save without a storybook ID.');
      const response = await storybookAPI.update(storybookId, {
        ...data,
        revision: authoritativeRevisionRef.current
      });
      const persisted = response.storybook as Storybook;
      authoritativeRevisionRef.current = persisted.revision;
      return persisted;
    });
  }

  const queueSave = useCallback((data: Partial<Storybook>) => (
    queueRef.current!.enqueue(data)
  ), []);

  const hasPendingSaves = useCallback(() => Boolean(queueRef.current?.hasPending), []);

  const runQueuedSave = useCallback(async (
    data: Partial<Storybook>,
    snapshot: EditorAutosaveSnapshot,
    thumbnailPage?: Page
  ): Promise<Storybook | null> => {
    activeSaveCountRef.current += 1;
    setSaving(true);
    setSaveConflict(null);
    try {
      const persisted = await queueSave(data);
      persistedSnapshotRef.current = snapshot;
      const currentOptions = optionsRef.current;
      currentOptions.setStorybook(local => adoptPersistedRevision(local, persisted, thumbnailPage));
      setLastSaved(new Date());
      return persisted;
    } catch (error) {
      const conflict = saveConflictFrom(error);
      if (conflict) setSaveConflict(conflict);
      console.error('Failed to save storybook:', error);
      return null;
    } finally {
      activeSaveCountRef.current = Math.max(0, activeSaveCountRef.current - 1);
      if (activeSaveCountRef.current === 0 && !queueRef.current?.hasPending) setSaving(false);
    }
  }, [queueSave, setSaving]);

  const performStorybookSave = useCallback(async (): Promise<Storybook | null> => {
    const currentOptions = optionsRef.current;
    const book = storybookRef.current;
    if (!book || !currentOptions.storybookId || currentOptions.isGenerating()) {
      if (currentOptions.isGenerating()) {
        console.log('⏸️ Save skipped - AI content generation in progress');
      }
      return null;
    }

    try {
      const pageIndex = clampPageIndex(currentOptions.currentPageIndex, book.pages.length);
      const thumbnail = await currentOptions.generateThumbnail(pageIndex);
      const pages = book.pages.map(page => ({ ...page }));
      const originalPage = pages[pageIndex];
      const thumbnailPage = thumbnail && originalPage && thumbnail !== originalPage.thumbnail
        ? { ...originalPage, thumbnail }
        : undefined;
      if (thumbnailPage) pages[pageIndex] = thumbnailPage;

      const data = {
        ...book,
        pages,
        plots: currentOptions.plots.length > 0 ? currentOptions.plots : undefined
      };
      const page = pages[pageIndex];
      if (page) {
        currentOptions.onPageSaved?.(
          pageIndex,
          currentOptions.getPageContentHash(page),
          thumbnail
        );
      }
      return runQueuedSave(
        data,
        createEditorAutosaveSnapshot({ ...book, pages }),
        thumbnailPage
      );
    } catch (error) {
      console.error('Failed to save storybook:', error);
      return null;
    }
  }, [runQueuedSave]);

  const saveStorybook = useCallback((): Promise<Storybook | null> => {
    const currentOptions = optionsRef.current;
    const book = storybookRef.current;
    if (!book) return performStorybookSave();
    const intentKey = JSON.stringify({
      book: createEditorAutosaveSnapshot(book),
      currentPageIndex: currentOptions.currentPageIndex,
      plots: currentOptions.plots
    });
    return matchingSaveRef.current.run(intentKey, performStorybookSave);
  }, [performStorybookSave]);

  const safeSaveWithMerge = useCallback(async (
    mergeChanges: (freshData: Storybook) => Partial<Storybook>,
    description = 'data'
  ): Promise<Storybook | null> => {
    const currentOptions = optionsRef.current;
    if (!currentOptions.storybookId) return null;
    try {
      console.log(`🔄 Fetching fresh data before saving ${description}...`);
      const freshResponse = await storybookAPI.getById(currentOptions.storybookId);
      const freshBook = freshResponse.storybook as Storybook;
      authoritativeRevisionRef.current = freshBook.revision;
      const savedData = await queueSave(mergeChanges(freshBook));
      authoritativeRevisionRef.current = savedData.revision;
      persistedSnapshotRef.current = createEditorAutosaveSnapshot(savedData);
      currentOptions.setStorybook(savedData);
      setLastSaved(new Date());
      console.log(`✅ Successfully saved ${description}`);
      return savedData;
    } catch (error) {
      console.error(`❌ Failed to save ${description}:`, error);
      throw error;
    }
  }, [queueSave]);

  // Establish a server-backed baseline exactly once for each loaded book. A
  // later local edit is compared to this snapshot instead of hashing a small,
  // incomplete subset of the document.
  useEffect(() => {
    const book = options.storybook;
    if (!book || !options.storybookId) return;
    if (baselineBookIdRef.current !== options.storybookId || !persistedSnapshotRef.current) {
      baselineBookIdRef.current = options.storybookId;
      persistedSnapshotRef.current = createEditorAutosaveSnapshot(book);
      authoritativeRevisionRef.current = book.revision;
    }
  }, [options.storybook, options.storybookId]);

  useEffect(() => {
    const currentOptions = optionsRef.current;
    const book = currentOptions.storybook;
    if (!book || currentOptions.loading || currentOptions.isGenerating()) return;
    const baseline = persistedSnapshotRef.current;
    if (!baseline) return;
    const patch = createEditorAutosavePatch(baseline, book);
    if (Object.keys(patch).length === 0) return;
    const snapshot = createEditorAutosaveSnapshot(book);
    const timer = window.setTimeout(() => {
      void runQueuedSave(patch, snapshot);
    }, currentOptions.autosaveDelayMs ?? 30000);
    return () => window.clearTimeout(timer);
  }, [options.storybook, options.loading, options.storybookId, runQueuedSave, savingState]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        void saveStorybook();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [saveStorybook]);

  return {
    saving: savingState,
    setSaving,
    lastSaved,
    setLastSaved,
    saveConflict,
    clearSaveConflict: () => setSaveConflict(null),
    saveStorybook,
    queueSave,
    safeSaveWithMerge,
    hasPendingSaves
  };
};
